import { createORPCClient } from '@orpc/client';
import {
  ContractRouterClient,
  inferRPCMethodFromContractRouter,
} from '@orpc/contract';
import { contractRouter } from '@erp/rpc';

import { RPCLink } from '@orpc/client/fetch';
import config from 'src/config';
import { createTanstackQueryUtils } from '@orpc/tanstack-query';
import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

/**
 * Create oRPC client with authentication integration
 */
const rpcLink = new RPCLink({
  url: `${config.apiBaseUrl}/rpc`,

  // Use the same fetch configuration as existing API calls
  // This ensures cookies are included for authentication
  fetch: (request: Request, init?: RequestInit) => {
    return fetch(request, {
      ...init,
      credentials: 'include', // Include cookies for session auth
    });
  },
  method: inferRPCMethodFromContractRouter(contractRouter),

  // Add error interceptor for auth handling
  interceptors: [
    // You can add error handling here if needed
    // For example, redirect to login on 401 errors
  ],
});

/**
 * oRPC client instance
 * Provides access to all backend RPC procedures
 */
const client: ContractRouterClient<typeof contractRouter> =
  createORPCClient(rpcLink);
export const orpc = createTanstackQueryUtils(client);

/**
 * Direct RPC client for imperative calls
 */
export const rpcClient = client;

type oRPCType = typeof orpc;

/**
 * Hook to return a function that invalidates RPC queries. This is useful for invalidating queries
 * after a mutation or query. This hook is mostly for devex and to prevent code duplication.
 *
 * @example
 * const invalidateRpcQueryFn = useInvalidateRpcQueryFn((orpc) => [orpc.memoryBank.list.queryOptions().queryKey]);
 *
 * const { mutateAsync: createNote } = useCreateMemoryBankNote();
 *
 * const handleCreateNote = async (noteContent: string) => {
 *   await createNote(noteContent);
 *   invalidateRpcQueryFn();
 * };
 *
 * @param fn - The function to call to get the query keys to invalidate.
 * @returns A function that invalidates the RPC queries.
 */
export function useInvalidateRpcQueryFn<T extends Record<string, unknown>>(
  fn: (orpc: oRPCType, input: T) => QueryKey[],
  dependencies: React.DependencyList = [],
) {
  const queryClient = useQueryClient();
  const memoizedFn = useCallback(fn, [fn, ...dependencies]);

  return useCallback(
    (input: T = {} as T) => {
      memoizedFn(orpc, input).forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey });
      });
    },
    [memoizedFn, queryClient],
  );
}

/**
 * Router type for the RPC client
 * This will be properly typed once the backend router is available
 */
export type RpcRouter = typeof client;
